import {
    CSSProperties,
    ReactElement,
    useEffect,
    useId,
    useMemo,
    useRef
} from 'react';
import styles from './common.module.scss';
import { UIAvatar } from '../ui-avatar';
import { UIIcon } from '../ui-icon';
import { UIMenu, UIMenuItem } from '../ui-menu';
import { UIPopover, UIPopoverMountSensor, UIPropoverRef } from '../ui-popover';
import { UISearchInput } from '../ui-search-input';
import { UIErrorState, UILoadingState } from '../ui-state';
import { UIText } from '../ui-text';
import { charcoal } from '../../styles';
import { UISelectDropDownZIndex } from '../z-index';
import { useInfiniteScrollSentinel } from '../../hooks';

export type UIPaginatedSelectOption = {
    value: string;
    label: string;
    subLabel?: string;
    image?: string;
    disabled?: boolean;
};

export type UIPaginatedSelectInputProps = {
    // Form-field chrome, mirroring UISelectInput.
    label?: string;
    description?: string;
    placeholder: string;
    required?: boolean;
    disabled?: boolean;
    errorText?: string;
    clearText?: string;
    menuWidth?: number;
    style?: CSSProperties;

    /**
     * The current selection. Supply `label` for a value that is not (yet) in
     * the loaded window — a device's existing member, for instance.
     */
    value?: UIPaginatedSelectOption;
    onChange: (option?: UIPaginatedSelectOption) => void;

    // The loaded pages, in server order.
    options: UIPaginatedSelectOption[];
    search: string;
    onSearchChange: (query: string) => void;
    searchPlaceholder: string;
    emptyText: string;
    errorStateText: string;
    isLoading: boolean;
    isError: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    onRetry?: () => void;
    /** Fired when the dropdown opens and closes, so callers can latch fetching. */
    onOpenChanged?: (open: boolean) => void;
};

/**
 * The server-paginated counterpart of `UISelectInput`: options arrive in pages
 * with server-side search, so the component never holds the full list.
 * Selection is therefore value-based, and every option ever seen is cached so a
 * selection made on an earlier page keeps its label once the window moves on.
 */
export function UIPaginatedSelectInput(
    props: UIPaginatedSelectInputProps
): ReactElement {
    const {
        label,
        description,
        placeholder,
        required = false,
        disabled = false,
        errorText = '',
        clearText,
        menuWidth = 360,
        style,
        value,
        onChange,
        options,
        search,
        onSearchChange,
        searchPlaceholder,
        emptyText,
        errorStateText,
        isLoading,
        isError,
        isFetchingNextPage,
        hasNextPage,
        onLoadMore,
        onRetry,
        onOpenChanged
    } = props;

    const id = useId();
    const popoverRef = useRef<UIPropoverRef>(null);

    // Every option ever received, plus whatever the caller supplied, so an
    // off-window selection keeps its label.
    const metaRef = useRef(new Map<string, UIPaginatedSelectOption>());
    useEffect(() => {
        options.forEach((option) => metaRef.current.set(option.value, option));
        if (value) {
            metaRef.current.set(value.value, value);
        }
    }, [options, value]);

    const selected = value
        ? (metaRef.current.get(value.value) ?? value)
        : undefined;

    // The selected row is pinned to the top so it stays reachable when the
    // search or the loaded window no longer contains it.
    const listOptions = useMemo(() => {
        if (!selected) {
            return options;
        }
        return [
            selected,
            ...options.filter((option) => option.value !== selected.value)
        ];
    }, [options, selected]);

    const { ref: bottomRef } = useInfiniteScrollSentinel({
        hasNextPage,
        isFetchingNextPage,
        onLoadMore
    });

    function select(option?: UIPaginatedSelectOption) {
        popoverRef.current?.close();
        onChange(option);
    }

    const showEmpty = !isLoading && !isError && listOptions.length === 0;

    const triggerClassNames = [styles.paginatedSelectTrigger];
    if (errorText) {
        triggerClassNames.push(styles.paginatedSelectTriggerError);
    }

    return (
        <div className={styles.container} style={style}>
            {label && (
                <div className={styles.labelContainer}>
                    <div>
                        <label className={styles.label} htmlFor={id}>
                            {label}
                        </label>
                        {required && (
                            <label
                                className={`${styles.label} ${styles.labelRequired}`}
                                htmlFor={id}
                            >
                                {' *'}
                            </label>
                        )}
                    </div>
                </div>
            )}

            <UIPopover
                ref={popoverRef}
                width={menuWidth}
                popoverPadding={4}
                placement="bottom_right"
                // The default matches UIModalZIndex, and this input is used
                // inside modals.
                zIndex={UISelectDropDownZIndex}
                triggerNode={
                    // Must be type="button": UIPopover clones the trigger and
                    // attaches onClick, and this is rendered inside forms.
                    <button
                        id={id}
                        type="button"
                        disabled={disabled}
                        className={triggerClassNames.join(' ')}
                    >
                        <span className={styles.paginatedSelectTriggerLabel}>
                            {selected?.image !== undefined && (
                                <UIAvatar
                                    img={selected.image}
                                    alt={selected.label}
                                    size={24}
                                />
                            )}
                            <UIText type={selected ? 'default' : 'default2'}>
                                {selected?.label ?? placeholder}
                            </UIText>
                        </span>
                        <UIIcon
                            name="chevron-down"
                            size={16}
                            color={charcoal}
                        />
                    </button>
                }
                onClickOutside={() => {
                    popoverRef.current?.close();
                }}
            >
                <UIPopoverMountSensor
                    // Reset on open, not on close: resetting on close changes
                    // the query key after the menu is gone and refetches the
                    // first page for nothing.
                    onMount={() => {
                        onSearchChange('');
                        onOpenChanged?.(true);
                    }}
                    onUnmount={() => onOpenChanged?.(false)}
                />
                <UIMenu
                    scroll
                    maxHeight={320}
                    header={
                        <UISearchInput
                            value={search}
                            placeholder={searchPlaceholder}
                            onChange={onSearchChange}
                        />
                    }
                >
                    {clearText && selected && (
                        <UIMenuItem
                            text={clearText}
                            leftIconName="x"
                            onClick={() => select(undefined)}
                        />
                    )}
                    {isLoading && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UILoadingState />
                        </div>
                    )}
                    {!isLoading && isError && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UIErrorState
                                title={errorStateText}
                                onRetry={onRetry}
                            />
                        </div>
                    )}
                    {showEmpty && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UIText type="default2">{emptyText}</UIText>
                        </div>
                    )}
                    {!isLoading &&
                        !isError &&
                        listOptions.map((option) => (
                            <UIMenuItem
                                key={option.value}
                                text={option.label}
                                description={option.subLabel}
                                leftAvatarProps={
                                    option.image !== undefined
                                        ? {
                                              img: option.image,
                                              alt: option.label,
                                              size: 24
                                          }
                                        : undefined
                                }
                                selected={option.value === selected?.value}
                                disabled={option.disabled}
                                onClick={() => select(option)}
                            />
                        ))}
                    {!isLoading && !isError && hasNextPage && (
                        <div
                            ref={bottomRef}
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: 8
                            }}
                        >
                            {isFetchingNextPage && <UILoadingState text="" />}
                        </div>
                    )}
                </UIMenu>
            </UIPopover>

            {errorText && (
                <div className={styles.errorTextContainer}>
                    <UIText variant="small" type="error">
                        {errorText}
                    </UIText>
                </div>
            )}
            {!errorText && description && (
                <div className={styles.descriptionTextContainer}>
                    <UIText variant="small" type="default2">
                        {description}
                    </UIText>
                </div>
            )}
        </div>
    );
}
